import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { GetExportRequestsQueryResponse } from "../types/GetExportRequests"

type GetExportRequestsClient = typeof client<
  GetExportRequestsQueryResponse,
  Error,
  never
>
type GetExportRequests = {
  data: GetExportRequestsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<GetExportRequestsClient>>
  client: {
    parameters: Partial<Parameters<GetExportRequestsClient>[0]>
    return: Awaited<ReturnType<GetExportRequestsClient>>
  }
}
export const getExportRequestsQueryKey = () =>
  [{ url: "/v1/export/export-requests" }] as const
export type GetExportRequestsQueryKey = ReturnType<
  typeof getExportRequestsQueryKey
>
export function getExportRequestsQueryOptions<
  TData = GetExportRequests["response"],
  TQueryData = GetExportRequests["response"],
>(
  options: GetExportRequests["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    GetExportRequests["response"],
    GetExportRequests["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = getExportRequestsQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        GetExportRequests["data"],
        GetExportRequests["error"]
      >({
        method: "get",
        url: `/v1/export/export-requests`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Get export requests list
 * @link /v1/export/export-requests
 */
export function useGetExportRequests<
  TData = GetExportRequests["response"],
  TQueryData = GetExportRequests["response"],
  TQueryKey extends QueryKey = GetExportRequestsQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        GetExportRequests["response"],
        GetExportRequests["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: GetExportRequests["client"]["parameters"]
  } = {}
): UseQueryResult<TData, GetExportRequests["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getExportRequestsQueryKey()
  const query = useQuery<
    GetExportRequests["data"],
    GetExportRequests["error"],
    TData,
    any
  >({
    ...getExportRequestsQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, GetExportRequests["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
